/**
 * Formatting utilities for numbers, currency, and measurements
 */

import type { CountryCode } from './location-detection';

/**
 * Format a number to a compact display (e.g., 3000 → "3k")
 */
export const formatCompactNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}m`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`;
  }
  return value.toString();
};

/**
 * Format a value with measure/unit and compact notation
 * @param value - Numeric value to format
 * @param measure - Measure/unit symbol (£, $, €, kg, hrs, etc.)
 * @returns Formatted string (e.g., "£3k", "$25k", "5kg")
 */
export const formatMeasuredValue = (
  value: number,
  measure: string = ''
): string => {
  const compactNumber = formatCompactNumber(value);
  return measure ? `${measure}${compactNumber}` : compactNumber;
};

/**
 * Format a measured range for display
 * @param min - Minimum value
 * @param max - Maximum value
 * @param measure - Measure/unit symbol
 * @returns Formatted range (e.g., "£3k to £5k", "5kg to 10kg")
 */
export const formatMeasuredRange = (
  min: number,
  max: number,
  measure: string = ''
): string => {
  const formattedMin = formatMeasuredValue(min, measure);
  const formattedMax = formatMeasuredValue(max, measure);

  return min === max ? formattedMin : `${formattedMin} to ${formattedMax}`;
};

/**
 * All European country codes (includes EU, EEA, and other European countries)
 */
export const EUROPEAN_COUNTRIES = [
  // EU Countries
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'HR', // Croatia
  'CY', // Cyprus
  'CZ', // Czech Republic
  'DK', // Denmark
  'EE', // Estonia
  'FI', // Finland
  'FR', // France
  'DE', // Germany
  'GR', // Greece
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LV', // Latvia
  'LT', // Lithuania
  'LU', // Luxembourg
  'MT', // Malta
  'NL', // Netherlands
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'SK', // Slovakia
  'SI', // Slovenia
  'ES', // Spain
  'SE', // Sweden
  // Non-EU European Countries
  'AL', // Albania
  'AD', // Andorra
  'BY', // Belarus
  'BA', // Bosnia and Herzegovina
  'IS', // Iceland
  'LI', // Liechtenstein
  'MK', // North Macedonia
  'MD', // Moldova
  'MC', // Monaco
  'ME', // Montenegro
  'NO', // Norway
  'RS', // Serbia
  'CH', // Switzerland
  'UA', // Ukraine
  'VA', // Vatican City
];

/**
 * Check if a country code is in Europe
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns true if the country is in Europe (excluding GB)
 */
export const isEuropeanCountry = (countryCode: CountryCode): boolean => {
  const code = countryCode.toUpperCase();
  return EUROPEAN_COUNTRIES.includes(code);
};

/**
 * Get currency symbol for a country code
 * Supports USD ($), GBP (£), and EUR (€)
 *
 * Rules:
 * - UK → GBP (£)
 * - Any European country → EUR (€)
 * - USA + Rest of World → USD ($) - default
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., "US", "GB", "DE")
 * @returns Currency symbol or default "$"
 */
export const getCurrencyForCountryCode = (countryCode: CountryCode): string => {
  const code = countryCode.toUpperCase();

  // GBP - United Kingdom only
  if (code === 'GB') {
    return '£';
  }

  // CAD - Canada
  if (code === 'CA') {
    return 'C$';
  }

  // EUR - Any European country
  if (EUROPEAN_COUNTRIES.includes(code)) {
    return '€';
  }

  // USD - USA and rest of world (default)
  return '$';
};

/**
 * Resolve measure to a display string
 * If the measure is a currency code (e.g., "GBP"), convert it to symbol (e.g., "£")
 * Otherwise, return the measure as-is
 * @param measure - Measure string (could be symbol, currency code, or unit)
 * @returns Display string for the measure
 */
export const resolveMeasure = (measure: string): string => {
  // Map currency codes to symbols
  const currencyCodeMap: Record<string, string> = {
    GBP: '£',
    USD: '$',
    EUR: '€',
    CAD: 'C$',
  };

  const upperMeasure = measure.toUpperCase();
  if (currencyCodeMap[upperMeasure]) {
    return currencyCodeMap[upperMeasure];
  }

  // Return as-is (could be a symbol or unit like "kg", "hrs")
  return measure;
};
